"use client";

import type { ResponsiveShowcaseContent } from "@/components/landing/registry/content";
import type { PublicSectionProps } from "@/components/landing/registry/types";
import type { DeviceId } from "@/components/landing/ResponsiveShowcase/types";
import { useEffect, useRef, useState } from "react";
import { pickBi } from "@/components/landing/registry/locale";
import DeviceStage from "@/components/landing/ResponsiveShowcase/DeviceStage";
import DeviceSwitcher from "@/components/landing/ResponsiveShowcase/DeviceSwitcher";
import { DEVICES } from "@/components/landing/ResponsiveShowcase/morphAnimation";
import {
	GlowBackground,
	Glyph,
	MotionReveal,
	SectionHeader,
	SectionShell,
	useSectionMotion,
} from "@/components/landing/shared";

interface InnerProps {
	title: string;
	subtitle: string;
	deviceLabels: Record<DeviceId, string>;
	shopLabel: string;
	bullets: string[];
}

const AUTO_CYCLE_MS = 3000;

function Inner(prop: InnerProps) {
	const { title, subtitle, deviceLabels, shopLabel, bullets } = prop;
	const motion = useSectionMotion();
	const [active, setActive] = useState<DeviceId>("desktop");
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const startAutoCycle = () => {
		if (intervalRef.current) clearInterval(intervalRef.current);
		intervalRef.current = setInterval(() => {
			setActive((prev) => {
				const nextIndex = (DEVICES.findIndex((d) => d.id === prev) + 1) % DEVICES.length;
				return DEVICES[nextIndex].id;
			});
		}, AUTO_CYCLE_MS);
	};

	useEffect(() => {
		startAutoCycle();
		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, []);

	const handleChange = (id: DeviceId) => {
		setActive(id);
		startAutoCycle();
	};

	return (
		<>
			<MotionReveal motion={motion}>
				<SectionHeader
					title={title}
					subtitle={subtitle}
					wrapperClassName="mx-auto max-w-2xl px-6 text-center"
				/>
			</MotionReveal>

			<MotionReveal motion={motion} delayOffset={100}>
				<div className="mt-10 flex justify-center">
					<DeviceSwitcher
						title={title}
						active={active}
						deviceLabels={deviceLabels}
						onChange={handleChange}
					/>
				</div>
			</MotionReveal>

			<MotionReveal motion={motion} delayOffset={160}>
				<div className="relative">
					<GlowBackground
						className="left-1/2 top-1/2 h-104 w-xl -translate-x-1/2 -translate-y-1/2 opacity-40"
						style={{
							background: "radial-gradient(circle, var(--brand-2), transparent 70%)",
						}}
					/>
					<DeviceStage
						active={active}
						shopLabel={shopLabel}
						deviceLabels={deviceLabels}
						onChange={handleChange}
					/>
				</div>
			</MotionReveal>

			{bullets.length > 0 && (
				<MotionReveal motion={motion} delayOffset={220}>
					<div className="mx-auto mt-10 flex max-w-3xl flex-wrap items-center justify-center gap-3 px-6">
						{bullets.map((bullet) => (
							<span
								key={bullet}
								className="inline-flex items-center gap-2 rounded-full border border-border bg-surface/60 px-4 py-2 text-sm font-medium"
							>
								<Glyph name="check" className="h-3.5 w-3.5 shrink-0 text-[var(--success)]" />
								{bullet}
							</span>
						))}
					</div>
				</MotionReveal>
			)}
		</>
	);
}

function ResponsiveShowcase(prop: PublicSectionProps<ResponsiveShowcaseContent>) {
	const { instance, locale } = prop;
	const { content, general, layout, style } = instance;

	const deviceLabels: Record<DeviceId, string> = {
		desktop: pickBi(content.deviceDesktopLabel, locale),
		tablet: pickBi(content.deviceTabletLabel, locale),
		mobile: pickBi(content.deviceMobileLabel, locale),
	};

	return (
		<SectionShell
			general={general}
			layout={layout}
			style={style}
			motion={instance.motion}
			className="jana-bg-base scroll-mt-20 overflow-hidden py-16 md:py-24"
		>
			<Inner
				title={pickBi(content.title, locale)}
				subtitle={pickBi(content.subtitle, locale)}
				deviceLabels={deviceLabels}
				shopLabel={pickBi(content.shopLabel, locale)}
				bullets={content.bullets.map((b) => pickBi(b, locale))}
			/>
		</SectionShell>
	);
}

export default ResponsiveShowcase;
